import { RecordIntent, intentSaysSomething, mergeIntent, parseRecordIntent, placesIn } from "./recordIntent";
import { isPermissionGranted } from "./navPermissions";
import { PagesPermissions } from "../types/Authorization";
import { linesFromText, ProposedPriceLine } from "./priceList";
import { readDetails } from "./companyDetails";
import type { FleetItem } from "./fleetList";
import type { AttachAction } from "./askAttachmentPlan";
import type { AfterCreateStep } from "./afterCreate";
import type { SimilarRecord } from "./similarRecord";
import type { LookedUpPlace } from "./placeLookup";
import { checkPhone } from "./phoneNumber";
import { westernDigits } from "./arabicText";

/**
 * Ask's creation conversation (user, 2026-10-15: "an interactive chat that
 * gets all it needs so it can create with good accuracy"): what the person
 * says is read by code (names never reach the AI, no credit), the record's
 * kind, name, where it's based (its address) and where it works (where its
 * rates apply) are gathered — one question at a time for what's missing or
 * unclear — CRX is checked for the record already there, and the last step
 * is a confirmation with the button that opens the form filled in. Every
 * later reply ("no, the name is ABC", "rates are for Qatar") updates it.
 */

export type SessionKind = "provider" | "client" | "company";

/** A list that came with the request: it goes on the new record's sheet once it's saved. */
export interface SessionList {
    kind: "price_list" | "fleet_list";
    file?: File;
    text?: string;
    lines?: ProposedPriceLine[];
    items?: FleetItem[];
    title?: string | null;
    /** The company the file names, read on this computer (OCR) — offered as the name, never assumed. */
    named?: string | null;
}

export type SessionQuestion = "kind" | "name" | "place" | "ratesFor" | "phone";

/** The quick reply that leaves a required field for the form. */
export const SKIP = "Skip — I'll add it on the form";

export interface CreationSession {
    intent: RecordIntent;
    list: SessionList | null;
    asked: SessionQuestion | null;
    /** The place asked about (not found as written). */
    askedPlace?: string;
    /** Looked up once per name / per place, as the person. */
    existing?: { name: string; record: SimilarRecord | null };
    places?: Record<string, LookedUpPlace | null>;
    /** Required fields the person chose to add on the form (warned, not blocked). */
    skipped?: SessionQuestion[];
}

export interface SessionStep {
    session: CreationSession;
    message: string;
    actions: AttachAction[];
    /** Quick replies, sent as the person's answer. */
    replies: string[];
}

export interface SessionDeps {
    pages: any[];
    findRecord: (name: string, kinds: SessionKind[]) => Promise<SimilarRecord | null>;
    lookUpPlace: (text: string) => Promise<LookedUpPlace | null>;
}

const NOUN: Record<SessionKind, string> = { provider: "provider", client: "client", company: "company" };
const CREATE_RIGHT: Record<SessionKind, string> = { provider: PagesPermissions.PROVIDERS_CREATE, client: PagesPermissions.CLIENTS_CREATE, company: PagesPermissions.COMPANIES_CREATE };
const asKind = (kind: RecordIntent["kind"]): SessionKind | null => (kind === "provider" || kind === "client" || kind === "company" ? kind : null);
const KIND_REPLY: Record<SessionKind, string> = { provider: "Provider", client: "Client", company: "Company" };
const KIND_HINT: Record<SessionKind, string> = { provider: "a provider (they sell to us)", client: "a client (we sell to them)", company: "another company" };

/**
 * Whether the person may start this kind of record (the conversation only starts when they may).
 * @param kind - Provider, client or company.
 * @param pages - The person's page permissions.
 */
export const canStartSession = (kind: SessionKind, pages: any[]): boolean => isPermissionGranted(pages, CREATE_RIGHT[kind]);

const ASKS_ORG = /^\s*(?:please\s+|pls\s+|can\s+you\s+|could\s+you\s+|would\s+you\s+|i\s+(?:want|need|would\s+like|'d\s+like)\s+to\s+|let'?s\s+|help\s+me\s+|go\s+ahead\s+and\s+)?(?:(create|add|make|register|set\s+up|open|start)\s+(?:a\s+|an\s+)?(?:new\s+)?|new\s+)(provider|supplier|vendor|client|customer|company)(?!'s)\b\s*([\s\S]*)$/i;

/**
 * The kind of record a typed request starts ("create a new provider ABC based
 * in Oman", "I want to add a client") — null when the words aren't one.
 * A bare "new client …" must name one ("new client meeting tomorrow" isn't).
 * @param text - What was typed in Ask.
 */
/** The same request in Arabic: "أنشئ مزود جديد اسمه …", "أضف عميل …" (a lead — عميل محتمل — is a typed command, not this). */
const ARABIC_ASKS_ORG = /^\s*(?:من فضلك\s+|لو سمحت\s+|ممكن\s+|بدي\s+|أريد\s+|اريد\s+)?(?:أضف|اضف|أضيف|اضيف|ضيف|أنشئ|انشئ|اعمل|سجل|سجّل|افتح)\s+(?:لي\s+)?(مزود(?:\s+خدمة)?|مورد|عميل(?!\s+محتمل)|زبون|شركة)(?:\s|$|[:،,])/;

export const sessionKindAsked = (text: string): SessionKind | null => {
    const arabic = ARABIC_ASKS_ORG.exec(String(text ?? ""));
    if (arabic) return /^(?:مزود|مورد)/.test(arabic[1]) ? "provider" : /^(?:عميل|زبون)/.test(arabic[1]) ? "client" : "company";
    const match = ASKS_ORG.exec(String(text ?? ""));
    if (!match) return null;
    if (!match[1] && !/^\s*[,:-]?\s*(?:called\s+|named\s+)?["“']?(?:[\p{Lu}\p{Lo}]|\+|00\d|$)/u.test(match[3])) return null;
    return /^(?:provider|supplier|vendor)$/i.test(match[2]) ? "provider" : /^(?:client|customer)$/i.test(match[2]) ? "client" : "company";
};

/** Start from the first words (and a list read from a file or pasted). A list is a provider's. */
export const startSession = (intent: RecordIntent, list: SessionList | null = null): CreationSession => ({
    intent: { ...intent, kind: asKind(intent.kind) ?? (list ? "provider" : null) },
    list,
    asked: null,
});

/**
 * The person's next words, read into the session — or null when they aren't
 * about it (a new question for Ask).
 */
export const replyToSession = (session: CreationSession, words: string): CreationSession | null => {
    const text = westernDigits(words).trim();
    if (!text) return null;
    if (session.asked === "kind") {
        const kind = /\b(provider|supplier|vendor)\b|مزود|مورد/i.test(text) ? "provider" : /\b(client|customer)\b|عميل|زبون/i.test(text) ? "client" : /\bcompany\b|شركة/i.test(text) ? "company" : null;
        if (kind) return { ...session, intent: { ...session.intent, kind }, asked: null };
    }
    if (session.asked && (/^\s*(?:skip|later|not now|no phone|i'?ll add it|add it (?:on|in) the form|leave it)/i.test(text) || /^\s*(?:تخطي|تخطى|لاحقا|لاحقاً|ليس الآن|مش هلق|بدون رقم|سأضيفه|بضيفه)/.test(text))) {
        return { ...session, asked: null, skipped: [...(session.skipped ?? []), session.asked] };
    }
    // the phone asked for, typed bare ("2412 3456" — its country code comes from where it's based)
    if (session.asked === "phone" && /^\+?[\d\s().-]{6,}$/.test(text) && text.replace(/\D/g, "").length >= 6) {
        return { ...session, intent: { ...session.intent, details: [session.intent.details, text].filter(Boolean).join("\n") }, asked: null };
    }
    const next = parseRecordIntent(text, { expect: session.asked === "name" ? "name" : session.asked === "ratesFor" ? "ratesFor" : undefined });
    // another record asked for ("create new contact Sara …"): a new request, not a reply
    if (next.asksNew && next.kind && next.kind !== session.intent.kind && session.intent.name) return null;
    // the place asked about, answered as a bare place ("Muscat, Oman")
    if (session.asked === "place" && session.askedPlace && !next.base && !next.coverage.length && !next.ratesFor) {
        const fixed = placesIn(text)[0] ?? text;
        const swap = (place: string) => (place === session.askedPlace ? fixed : place);
        const intent = { ...session.intent, base: session.intent.base ? swap(session.intent.base) : null, coverage: session.intent.coverage.map(swap) };
        return { ...session, intent, asked: null, askedPlace: undefined };
    }
    if (!intentSaysSomething(next)) return null;
    const intent = mergeIntent(session.intent, { ...next, kind: asKind(next.kind) ?? null });
    return { ...session, intent, asked: null, askedPlace: undefined, existing: intent.name === session.intent.name ? session.existing : undefined };
};

const priceLines = (list: SessionList | null): ProposedPriceLine[] =>
    !list || list.kind !== "price_list" ? [] : list.lines?.length ? list.lines : list.text ? linesFromText(list.text, { services: [], places: [], resources: [] }).filter((line) => line.amount !== null) : [];

/** A phone, an email, a website in the details given ("+968 2412 3456") — the latest of each: a correction wins. */
const reachOf = (details: string) => {
    const lines = details.split("\n").map((line) => line.trim()).filter(Boolean).reverse();
    return {
        phone: lines.find((line) => /\d[\d\s().-]{6,}\d/.test(line) && !/@/.test(line)) ?? null,
        email: lines.find((line) => /[\w.+-]+@[\w-]+\./.test(line)) ?? null,
        website: lines.find((line) => /^www\.|^https?:/i.test(line)) ?? null,
    };
};

/** One row of the checklist: ✓ given · ⚠ missing (required) · – not given (optional). */
const row = (label: string, value: string | null, options: { required?: boolean; predicted?: boolean; skipped?: boolean; note?: string } = {}) =>
    value
        ? `• ✓ ${label}: ${value}${options.predicted ? " (predicted — check it)" : ""}${options.note ? ` ${options.note}` : ""}`
        : options.required
            ? `• ⚠ ${label}: missing — required${options.skipped ? ", add it on the form before saving" : ""}`
            : `• – ${label}: not given`;

/** The next step: a question, a choice (it's already in CRX), or the checklist with the button. */
export const advanceSession = async (input: CreationSession, deps: SessionDeps): Promise<SessionStep> => {
    const session: CreationSession = { ...input, places: { ...(input.places ?? {}) }, skipped: [...(input.skipped ?? [])] };
    const { intent, list } = session;
    const kind = asKind(intent.kind);
    const skipped = (question: SessionQuestion) => session.skipped!.includes(question);
    const ask = (asked: SessionQuestion, message: string, replies: string[] = [], actions: AttachAction[] = [], askedPlace?: string): SessionStep =>
        ({ session: { ...session, asked, askedPlace }, message, replies, actions });

    // only the kinds this person may start are offered
    const allowed = (["provider", "client", "company"] as SessionKind[]).filter((option) => canStartSession(option, deps.pages));
    const askKind = () => ask("kind", `Is it ${allowed.map((option) => KIND_HINT[option]).join(allowed.length > 2 ? ", " : " or ").replace(/, ([^,]+)$/, " or $1")}?`, allowed.map((option) => KIND_REPLY[option]));
    if (!kind || !allowed.includes(kind)) {
        if (!allowed.length) return { session: { ...session, asked: null }, message: "Open the record's page to add it there.", replies: [], actions: [] };
        return askKind();
    }
    const noun = NOUN[kind];
    if (!intent.name) {
        const named = list?.named?.trim() || null;
        return ask("name", `What's the ${noun}'s name?${named ? ` The file names "${named}" — tap it if that's them.` : ""} You can add where it's based and where it works — for example "ABC, based in Muscat, works in Dubai and Qatar".`, named ? [named] : []);
    }

    // already in CRX? (searched as the person; once per name)
    if (!session.existing || session.existing.name !== intent.name) {
        session.existing = { name: intent.name, record: await deps.findRecord(intent.name, [kind]).catch(() => null) };
    }
    const existing = session.existing.record;

    // the places, as the lookups know them (the closest when not exact: predicted)
    const names = [intent.base, ...intent.coverage, ...(Array.isArray(intent.ratesFor) ? intent.ratesFor : [])].filter((name): name is string => !!name);
    for (const name of names) {
        if (!(name in session.places!)) session.places![name] = await deps.lookUpPlace(name).catch(() => null);
    }
    const unknown = names.find((name) => !session.places![name]);
    if (unknown) {
        return ask("place", `I couldn't find "${unknown}" — check the spelling, or add its country (for example "${unknown}, Oman").`, [], [], unknown);
    }
    const base = intent.base ? session.places![intent.base] : null;
    const coverage = Array.from(new Map(intent.coverage.map((name) => session.places![name]!).map((place) => [place.key, place])).values());

    // which places a price list's rates go in, when it isn't clear
    const lines = priceLines(list);
    const ratePlaces = (): LookedUpPlace[] => {
        if (intent.ratesFor === "all") return coverage;
        if (Array.isArray(intent.ratesFor)) return intent.ratesFor.map((name) => session.places![name]).filter((place): place is LookedUpPlace => !!place);
        if (coverage.length === 1) return coverage;
        return coverage.length === 0 && base ? [base] : [];
    };
    if (!existing && list?.kind === "price_list" && coverage.length >= 2 && !intent.ratesFor && !lines.some((line) => line.place)) {
        return ask("ratesFor", `Do these rates apply in ${coverage.map((place) => place.label).join(" or ")} — or all of them?`, [...coverage.map((place) => place.label), "All of them"]);
    }

    // the required fields: asked once each; "skip" leaves it for the form, with a warning
    const reach = reachOf(intent.details);
    // the phone written the international way, its country code from where it's based when typed without one
    const iso2 = base?.country?.iso2Code ?? null;
    const phone = reach.phone ? checkPhone(reach.phone, iso2) : null;
    if (!existing && !reach.phone && !skipped("phone")) {
        return ask("phone", `What's ${intent.name}'s phone number? A ${noun} needs at least one — or skip it and add it on the form.`, [SKIP]);
    }
    const phoneNote = !phone ? undefined
        : !phone.valid ? `— doesn't look like a full${iso2 && phone.local ? ` ${base!.country.name}` : ""} number; check it on the form`
            : phone.local ? `(country code from ${base!.country.name} — predicted, check it)` : undefined;

    // the record and what comes after it
    const places = ratePlaces();
    const placedLines = places.length && lines.length
        ? places.flatMap((place) => lines.map((line) => ({ ...line, context: line.context ? `${place.label} · ${line.context}` : place.label })))
        : lines;
    const then: AfterCreateStep[] = [];
    const aiLines = !!list?.lines?.length;
    const placeLabels = places.length ? places.map((place) => place.label) : undefined;
    if (list?.kind === "price_list") {
        then.push(aiLines
            ? { type: "price_list", file: list.file, text: list.text, lines: placedLines }
            : { type: "price_list", file: list.file, text: list.text, places: placeLabels });
    }
    if (list?.kind === "fleet_list") then.push({ type: "fleet_list", file: list.file, text: list.text, items: list.items });
    const contactText = intent.contact ? intent.contact.replace(/\s*[,;]\s*/g, "\n").replace(/\s+(?=(?:\+|00)\d|0\d[\d\s().-]{6,}\d|[\w.+-]+@)/g, "\n") : null;
    if (contactText && isPermissionGranted(deps.pages, PagesPermissions.CONTACTS_CREATE)) then.push({ type: "contact", text: contactText });
    const extra = list?.kind === "price_list" ? " + rates" : list?.kind === "fleet_list" ? " + fleet" : contactText ? " + contact" : "";
    // where it works, and the name as written in Arabic (kept in its notes — user decision 2026-10-15)
    const notes = [coverage.length ? `Works in: ${coverage.map((place) => place.label).join("; ")}` : null, intent.nameArabic ? `Arabic name: ${intent.nameArabic}` : null].filter(Boolean).join("\n") || undefined;
    const missing = reach.phone ? [] : ["phone"];
    const createIt: AttachAction = {
        label: `Fill with AI: new ${noun} "${intent.name}"${extra}`, icon: "stars",
        go: {
            type: "record_new", kind, name: intent.name, then,
            phone: phone?.number ?? reach.phone ?? undefined, email: reach.email ?? undefined, website: reach.website ?? undefined,
            address: base ? { label: base.label, country: base.country, city: base.city } : undefined, notes,
        },
        title: `Open a new ${noun} filled in with this — you check it${missing.length ? `, add the ${missing.join(" and ")}` : ""} and save${then.length ? "; what comes with it opens next" : ""}.`,
    };

    const checklist = [
        row("Kind", `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`, { predicted: !input.intent.kind && !!list && kind === "provider", note: list && !input.intent.kind ? "(from the price list)" : undefined }),
        row("Name", intent.name, { required: true, note: intent.nameArabic ? "(from Arabic — check spelling)" : undefined }),
        row("Phone", phone?.number ?? reach.phone, { required: true, skipped: skipped("phone"), note: phoneNote }),
        row("Email", reach.email),
        row("Based in (address)", base?.label ?? null, { predicted: base ? !base.exact : false }),
        row("Works in", coverage.length ? coverage.map((place) => `${place.label}${place.exact ? "" : " (predicted)"}`).join(", ") : null),
        list?.kind === "price_list" ? row("Price list", `${list.file ? `${list.file.name} — ` : ""}${lines.length ? `${lines.length} line${lines.length === 1 ? "" : "s"}` : "read on its rate sheet"}${places.length ? `, rates in ${places.map((place) => place.label).join(" and ")}` : ""}`) : null,
        list?.kind === "fleet_list" ? row("Fleet list", `${list.file ? list.file.name : "pasted"}${list.items?.length ? ` — ${list.items.length} items` : ""}`) : null,
        intent.contact ? row("Then a contact", readDetails(contactText ?? "").person ?? contactText?.split("\n")[0] ?? intent.contact) : null,
    ].filter(Boolean).join("\n");
    const change = "Anything to change? Just say it — for example \"the name is ABC Trading\", \"based in Muscat\", \"phone +968 2412 3456\" or \"rates are for Qatar\".";

    if (existing) {
        // already in CRX: use it — or start a new one anyway
        const useIt: AttachAction = list?.kind === "price_list"
            ? { label: `Fill with AI: ${existing.name}'s rates`, icon: "stars", go: { type: "price_list", file: list.file as File, providerId: existing.id, ...(aiLines ? { lines: placedLines } : { places: placeLabels }) }, title: `Put the list on ${existing.name}'s rate sheet.` }
            : list?.kind === "fleet_list"
                ? { label: `Fill with AI: ${existing.name}'s fleet`, icon: "stars", go: list.file ? { type: "fleet_file", providerId: existing.id, file: list.file, items: list.items } : { type: "fleet_text", providerId: existing.id, text: list.text ?? "" }, title: `Put the list in ${existing.name}'s assets.` }
                : { label: `Open ${existing.name}`, icon: "box-arrow-up-right", go: { type: "link", to: existing.url }, title: `Open ${existing.name} in CRX.` };
        return {
            session: { ...session, asked: null },
            message: `${existing.exact ? `${existing.name} is already in CRX` : `The closest in CRX is ${existing.name} (predicted)`} — use it, or start "${intent.name}" as a new ${noun}?`,
            replies: [],
            actions: [useIt, createIt],
        };
    }
    const warning = missing.length ? `\nStill missing: ${missing.join(", ")} — the form opens anyway; add it there before saving.` : "";
    return { session: { ...session, asked: null }, message: `Here's what I have for the new ${noun}:\n${checklist}${warning}\n${change}`, replies: [], actions: [createIt] };
};

/** What the session has gathered, in plain words, as context when the talk turns to Ask. */
export const describeSession = (session: CreationSession): string => {
    const { intent, list } = session;
    return [
        `The user is creating a ${intent.kind ?? "record"} in CRX with CRX's help (read by CRX, not by you).`,
        list ? `A ${list.kind === "price_list" ? "price list" : "fleet list"} came with it${list.title ? ` (${list.title})` : ""}.` : null,
        intent.base ? `Based in: ${intent.base}.` : null,
        intent.coverage.length ? `Works in: ${intent.coverage.join(", ")}.` : null,
    ].filter(Boolean).join(" ");
};
